import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { Icon } from "@iconify/react";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import { SwipeableList } from "react-swipeable-list";
import "react-swipeable-list/dist/styles.css";
import { Get } from "../api/api";
import { canCloseRegister } from "../api/cash";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PinGuard } from "@/components/PinGuard";
import { RegisterCloseDialog } from "../components/RegisterCloseDialog";
import { TicketLine } from "../components/TicketLine";
import { UserContext } from "../context/user";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";

const ticketFilters = [
  { key: "all", label: "Todos" },
  { key: "active", label: "Activos" },
  { key: "returns", label: "Devoluciones" },
];

function getPaymentIcon(method) {
  if (!method) return "mdi:currency-eur";
  const name = method.toLowerCase();
  if (name.includes("efectivo") || name.includes("metalico")) return "mdi:cash";
  if (name.includes("tarjeta")) return "mdi:credit-card-outline";
  if (name.includes("bizum")) return "mdi:cellphone-nfc";
  if (name.includes("transferencia")) return "mdi:bank-transfer";
  return "mdi:currency-eur";
}

function formatCurrency(value) {
  return `${Number(value || 0).toFixed(2)}€`;
}

function TicketsSkeleton() {
  return (
    <div className="divide-y divide-neutral-200">
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="flex h-24 animate-pulse items-center gap-4 px-1">
          <div className="h-5 w-5 rounded-full bg-neutral-200" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-4 w-36 rounded bg-neutral-200" />
            <div className="h-3 w-48 rounded bg-neutral-100" />
          </div>
          <div className="h-6 w-24 rounded bg-neutral-200" />
        </div>
      ))}
    </div>
  );
}

function CashControlSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-28 animate-pulse border-y border-neutral-200" />
      <div className="space-y-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="h-12 animate-pulse border-b border-neutral-200" />
        ))}
      </div>
      <div className="h-40 animate-pulse border-y border-neutral-200" />
    </div>
  );
}

function MetricCard({ icon, label, value, tone = "neutral" }) {
  const tones = {
    neutral: "text-neutral-400",
    blue: "text-blue-500",
    amber: "text-amber-500",
  };

  return (
    <div className="min-w-0 border-l border-neutral-200 pl-3 first:border-l-0 first:pl-0">
      <div className={`flex items-center gap-1.5 ${tones[tone]}`}>
        <Icon icon={icon} className="text-base" />
        <span className="truncate text-xs font-medium">{label}</span>
      </div>
      <div className="mt-1 truncate text-lg font-semibold text-neutral-900">
        {value}
      </div>
    </div>
  );
}

function HourlySalesChart({ data }) {
  if (!data.length) return null;

  const maxTotal = Math.max(...data.map((item) => item.total));

  return (
    <section className="border-t border-neutral-200 pt-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold text-neutral-900">Ventas por hora</h3>
          <p className="text-xs text-neutral-500">Ticket acumulado</p>
        </div>
        <Icon icon="mdi:chart-bar" className="text-xl text-neutral-400" />
      </div>

      <div className="mt-5 flex h-32 items-end gap-1.5">
        {data.map((item) => {
          const percentage = maxTotal > 0 ? (item.total / maxTotal) * 100 : 0;
          return (
            <div
              key={item.hour}
              className="flex h-full min-w-0 flex-1 flex-col items-center justify-end"
              title={`${item.hour}:00 · ${formatCurrency(item.total)}`}
            >
              <span className="mb-1 text-[10px] font-semibold text-neutral-500">
                {Math.round(item.total)}
              </span>
              <div
                className="w-full max-w-7 rounded-t-md bg-blue-500 transition-all"
                style={{ height: `${Math.max(percentage, 5)}%` }}
              />
              <span className="mt-1.5 text-[10px] text-neutral-400">
                {item.hour}h
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function CashPanel({
  analytics,
  analyticsLoading,
  canClose,
  hourlyData,
  kpis,
  onCloseRegister,
}) {
  const paymentMethods = analytics?.data || [];
  const totalCash = paymentMethods.reduce(
    (total, method) => total + (Number(method.total) || 0),
    0,
  );

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-3 border-b border-neutral-300 pb-5">
        <div>
          <h1 className="text-xl font-semibold text-neutral-900">Control de caja</h1>
          <p className="mt-1 text-sm text-neutral-500">Resumen del turno actual</p>
        </div>
        {analytics?.success && (
          <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-700">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            Abierta
          </span>
        )}
      </header>

      {analyticsLoading ? (
        <CashControlSkeleton />
      ) : analytics?.success ? (
        <>
          <section className="border-b border-neutral-200 pb-6">
            <p className="text-sm text-neutral-500">Total en caja</p>
            <p className="mt-2 text-4xl font-semibold tracking-tight text-neutral-900">
              {formatCurrency(totalCash)}
            </p>
            <p className="mt-3 text-xs text-neutral-400">
              {paymentMethods.length}{" "}
              {paymentMethods.length === 1 ? "método de pago" : "métodos de pago"}
            </p>
          </section>

          <section>
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-neutral-700">Métodos de pago</h2>
              <span className="text-xs text-neutral-400">{paymentMethods.length}</span>
            </div>
            <div className="divide-y divide-neutral-200 border-y border-neutral-200">
              {paymentMethods.map((method, index) => {
                const name = method.tipopago?.name || "Otro";
                return (
                  <div
                    key={method.tipopago?.id || index}
                    className="flex min-h-14 items-center gap-3 py-2"
                  >
                    <Icon
                      icon={getPaymentIcon(name)}
                      className="text-lg text-neutral-400"
                    />
                    <span className="min-w-0 flex-1 truncate text-sm text-neutral-600">
                      {name}
                    </span>
                    <span className="shrink-0 font-semibold text-neutral-900">
                      {formatCurrency(method.total)}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="border-b border-neutral-200 pb-5">
            <h2 className="mb-4 text-sm font-semibold text-neutral-700">Resumen</h2>
            <div className="grid grid-cols-3 gap-3">
              <MetricCard
                icon="mdi:receipt-text-outline"
                label="Activos"
                value={kpis.count}
                tone="blue"
              />
              <MetricCard
                icon="mdi:chart-line"
                label="Ticket medio"
                value={formatCurrency(kpis.avg)}
              />
              <MetricCard
                icon="mdi:undo-variant"
                label="Devoluciones"
                value={kpis.returns}
                tone="amber"
              />
            </div>
          </section>

          <HourlySalesChart data={hourlyData} />

          {canClose ? (
            <Button
              type="button"
              variant="outline"
              onClick={onCloseRegister}
              className="h-14 w-full border-red-200 text-base text-red-600 hover:bg-red-50 hover:text-red-700"
            >
              <Icon icon="mdi:lock-check-outline" className="mr-2 text-xl" />
              Cerrar caja
            </Button>
          ) : (
            <p className="text-xs text-neutral-400">
              El cierre y el arqueo de caja no están incluidos en tu plan.
            </p>
          )}
        </>
      ) : (
        <section className="border-y border-neutral-200 py-5 text-amber-800">
          <div className="flex items-start gap-3">
            <Icon icon="mdi:alert-outline" className="mt-0.5 text-xl" />
            <div>
              <h2 className="font-semibold">Caja no disponible</h2>
              <p className="mt-1 text-sm">
                No se han podido cargar los datos del turno actual.
              </p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function TicketsEmptyState({ hasActiveFilters }) {
  return (
    <div className="flex min-h-80 items-center justify-center p-8 text-center">
      <div>
        <Icon
          icon={hasActiveFilters ? "mdi:magnify-close" : "mdi:receipt-text-outline"}
          className="text-5xl text-neutral-300"
        />
        <h2 className="mt-4 text-lg font-semibold text-neutral-800">
          {hasActiveFilters ? "Sin resultados" : "Todavía no hay tickets"}
        </h2>
        <p className="mx-auto mt-1 max-w-sm text-sm leading-6 text-neutral-500">
          {hasActiveFilters
            ? "Prueba con otra búsqueda o cambia el filtro seleccionado."
            : "Cuando completes una venta, aparecerá aquí automáticamente."}
        </p>
        {!hasActiveFilters && (
          <p className="mt-4 text-xs text-neutral-400">Actualización automática</p>
        )}
      </div>
    </div>
  );
}

export function Tickets() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedTickets, setSelectedTickets] = useState(new Set());
  const [bulkReturning, setBulkReturning] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const confirm = useConfirmDialog();
  const { user } = useContext(UserContext);

  const shopId = localStorage.getItem("shop_id");
  const exerciseId = localStorage.getItem("exercise_id");

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search), 400);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setSelectedTickets(new Set());
  }, [debouncedSearch, statusFilter]);

  const {
    data: ticketsData,
    isLoading: ticketsLoading,
    isFetching: ticketsFetching,
    refetch: refetchTickets,
  } = useQuery({
    queryKey: ["tickets", shopId, exerciseId, debouncedSearch],
    queryFn: () => {
      let url = `/tickets?shop_id=${shopId}&exercise_id=${exerciseId}`;
      if (debouncedSearch) {
        url += `&search=${encodeURIComponent(debouncedSearch)}`;
      }
      return Get({ request: url });
    },
  });

  const {
    data: analytics,
    isLoading: analyticsLoading,
    refetch: reloadAnalytics,
  } = useQuery({
    queryKey: ["registerAnalytics", shopId, exerciseId],
    queryFn: () =>
      Get({
        request: `/analytics/current_register?shop_id=${shopId}&exercise_id=${exerciseId}`,
      }),
  });

  const tickets = useMemo(
    () =>
      Array.isArray(ticketsData)
        ? ticketsData
        : (ticketsData?.tickets ?? []),
    [ticketsData],
  );

  const isReturned = (ticket) => ticket.returned || ticket.is_returned;
  const isActive = (ticket) => !ticket.returned && !ticket.is_returned;

  const filterCounts = useMemo(
    () => ({
      all: tickets.length,
      active: tickets.filter(isActive).length,
      returns: tickets.filter(isReturned).length,
    }),
    [tickets],
  );

  const filteredTickets = useMemo(() => {
    if (statusFilter === "active") return tickets.filter(isActive);
    if (statusFilter === "returns") return tickets.filter(isReturned);
    return tickets;
  }, [statusFilter, tickets]);

  const returnableTickets = useMemo(
    () => filteredTickets.filter(isActive),
    [filteredTickets],
  );

  const allSelected =
    returnableTickets.length > 0 &&
    returnableTickets.every((ticket) => selectedTickets.has(ticket.id));

  const selectedTotal = useMemo(
    () =>
      tickets.reduce(
        (total, ticket) =>
          selectedTickets.has(ticket.id)
            ? total + (parseFloat(ticket.total) || 0)
            : total,
        0,
      ),
    [selectedTickets, tickets],
  );

  const nonReturnedTickets = useMemo(() => tickets.filter(isActive), [tickets]);

  const kpis = useMemo(() => {
    const count = nonReturnedTickets.length;
    const total = nonReturnedTickets.reduce(
      (sum, ticket) => sum + (parseFloat(ticket.total) || 0),
      0,
    );
    const average = count > 0 ? total / count : 0;
    const returns = tickets.filter(isReturned).length;
    return { count, average, returns };
  }, [nonReturnedTickets, tickets]);

  const hourlyData = useMemo(() => {
    if (nonReturnedTickets.length === 0) return [];
    const salesByHour = {};

    for (const ticket of nonReturnedTickets) {
      const hour = dayjs(ticket.created_at).hour();
      salesByHour[hour] =
        (salesByHour[hour] || 0) + (parseFloat(ticket.total) || 0);
    }

    return Object.entries(salesByHour)
      .map(([hour, total]) => ({ hour: Number(hour), total }))
      .filter((item) => item.total > 0)
      .sort((a, b) => a.hour - b.hour);
  }, [nonReturnedTickets]);

  const handleRefetch = useCallback(() => {
    refetchTickets();
    reloadAnalytics();
  }, [refetchTickets, reloadAnalytics]);

  const toggleSelect = useCallback((id) => {
    setSelectedTickets((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  function toggleSelectAll() {
    if (allSelected) {
      setSelectedTickets(new Set());
      return;
    }
    setSelectedTickets(new Set(returnableTickets.map((ticket) => ticket.id)));
  }

  function bulkReturn() {
    const count = selectedTickets.size;
    if (!count) return;

    confirm({
      title: `¿Devolver ${count} ticket${count > 1 ? "s" : ""}?`,
      description:
        "Esta acción no se puede deshacer. Se generarán los tickets de devolución.",
      okText: `Devolver ${count} ticket${count > 1 ? "s" : ""}`,
      cancelText: "Cancelar",
      variant: "danger",
      onOk: async () => {
        setBulkReturning(true);
        try {
          const results = await Promise.allSettled(
            [...selectedTickets].map((id) =>
              Get({
                request: `/tickets/${id}/return?shop_id=${shopId}`,
              }),
            ),
          );
          const succeeded = results.filter(
            (result) => result.status === "fulfilled",
          ).length;
          const failed = results.filter(
            (result) => result.status === "rejected",
          ).length;

          if (failed === 0) {
            toast.success(
              `${succeeded} ticket${succeeded > 1 ? "s" : ""} devuelto${succeeded > 1 ? "s" : ""} correctamente`,
            );
          } else {
            toast.error(
              `${succeeded} devuelto${succeeded > 1 ? "s" : ""}, ${failed} fallido${failed > 1 ? "s" : ""}`,
            );
          }

          setSelectedTickets(new Set());
          handleRefetch();
        } finally {
          setBulkReturning(false);
        }
      },
    });
  }

  // El arqueo vive en un diálogo aparte: cerrar es irreversible y hay que contar
  // el efectivo antes, no después.
  function closeRegister() {
    setCloseDialogOpen(true);
  }

  const canClose = canCloseRegister(user);

  const hasActiveFilters = Boolean(debouncedSearch) || statusFilter !== "all";
  const cashTotal = (analytics?.data || []).reduce(
    (total, method) => total + (Number(method.total) || 0),
    0,
  );

  return (
    <PinGuard
      title="Tickets protegidos"
      description="Introduce el PIN de acceso para consultar las ventas."
    >
      <div className="absolute inset-0 overflow-hidden bg-neutral-100 px-4 py-3 select-none sm:px-6 sm:py-4">
      <div className="flex h-full min-w-0 gap-6">
        <aside className="hidden h-full w-[24rem] shrink-0 overflow-y-auto border-r border-neutral-300 pr-5 lg:block xl:w-[27rem]">
          <CashPanel
            analytics={analytics}
            analyticsLoading={analyticsLoading}
            canClose={canClose}
            hourlyData={hourlyData}
            kpis={kpis}
            onCloseRegister={closeRegister}
          />
        </aside>

        <section className="flex min-w-0 flex-1 flex-col">
          {!analyticsLoading && analytics?.success && (
            <div className="mb-3 flex items-center justify-between gap-3 border-b border-neutral-300 pb-3 lg:hidden">
              <div>
                <p className="text-xs text-neutral-500">Total en caja</p>
                <p className="truncate text-xl font-semibold text-neutral-900">
                  {formatCurrency(cashTotal)}
                </p>
              </div>
              {canClose && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={closeRegister}
                  className="h-12 shrink-0 text-red-600 hover:bg-red-50 hover:text-red-700"
                >
                  <Icon icon="mdi:lock-check-outline" className="text-xl" />
                  <span className="hidden sm:inline">Cerrar caja</span>
                </Button>
              )}
            </div>
          )}

          <header className="shrink-0 border-b border-neutral-300 pb-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-semibold text-neutral-900">Tickets</h1>
                  <span className="text-sm font-medium text-neutral-400">
                    {tickets.length}
                  </span>
                </div>
                <p className="text-sm text-neutral-500">
                  Consulta, reimprime o devuelve ventas del turno.
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {returnableTickets.length > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={toggleSelectAll}
                    className="h-12 px-3 text-sm text-neutral-600"
                  >
                    <Icon
                      icon={allSelected ? "mdi:checkbox-blank-outline" : "mdi:select-all"}
                      className="mr-2 text-lg"
                    />
                    <span className="hidden sm:inline">
                      {allSelected ? "Deseleccionar" : "Seleccionar todo"}
                    </span>
                  </Button>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleRefetch}
                  disabled={ticketsFetching}
                  aria-label="Actualizar tickets"
                  className="h-12 w-12 shrink-0 px-0 text-neutral-500"
                >
                  <Icon
                    icon="mdi:refresh"
                    className={`text-xl ${ticketsFetching ? "animate-spin" : ""}`}
                  />
                </Button>
              </div>
            </div>

            <div className="relative mt-4">
              <Icon
                icon="mdi:magnify"
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xl text-neutral-400"
              />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar por referencia o cliente…"
                aria-label="Buscar tickets"
                className="h-14 rounded-none border-0 border-b border-neutral-300 bg-transparent pl-10 pr-12 text-base shadow-none focus:border-blue-500 focus:ring-0 focus-visible:ring-0"
              />
              {search && (
                <button
                  type="button"
                  aria-label="Limpiar búsqueda"
                  onClick={() => setSearch("")}
                  className="absolute right-0 top-1/2 flex h-11 w-11 -translate-y-1/2 touch-manipulation items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-white/70 hover:text-neutral-700 active:scale-95"
                >
                  <Icon icon="mdi:close" className="text-xl" />
                </button>
              )}
            </div>

            <div className="mt-3 flex border-b border-neutral-300">
              {ticketFilters.map((filter) => {
                const active = statusFilter === filter.key;
                return (
                  <button
                    type="button"
                    key={filter.key}
                    onClick={() => setStatusFilter(filter.key)}
                    className={`-mb-px flex h-12 min-w-0 flex-1 touch-manipulation items-center justify-center gap-1.5 border-b-2 px-2 text-sm font-medium transition-colors active:scale-[0.98] ${
                      active
                        ? "border-blue-600 text-blue-600"
                        : "border-transparent text-neutral-500 hover:text-neutral-800"
                    }`}
                  >
                    <span className="truncate">{filter.label}</span>
                    <span
                      className={`text-[10px] ${
                        active ? "text-blue-600" : "text-neutral-400"
                      }`}
                    >
                      {filterCounts[filter.key]}
                    </span>
                  </button>
                );
              })}
            </div>
          </header>

          <div data-bottom-nav-scroll className="mt-2 min-h-0 flex-1 overflow-y-auto pr-1">
            {ticketsLoading ? (
              <TicketsSkeleton />
            ) : filteredTickets.length === 0 ? (
              <TicketsEmptyState hasActiveFilters={hasActiveFilters} />
            ) : (
              <div className="pb-[calc(0.5rem_+_var(--bottom-nav-reserve))]">
                <div className="mb-1 flex items-center justify-between px-1">
                  <span className="text-sm font-medium text-neutral-500">
                    {filteredTickets.length}{" "}
                    {filteredTickets.length === 1 ? "ticket" : "tickets"}
                  </span>
                  {hasActiveFilters && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearch("");
                        setStatusFilter("all");
                      }}
                      className="h-10 px-2 text-sm font-medium text-blue-600 hover:text-blue-700"
                    >
                      Limpiar filtros
                    </button>
                  )}
                </div>
                <SwipeableList>
                  {filteredTickets.map((ticket) => (
                    <TicketLine
                      key={ticket.id}
                      ticket={ticket}
                      refetch={handleRefetch}
                      selected={selectedTickets.has(ticket.id)}
                      onSelect={toggleSelect}
                    />
                  ))}
                </SwipeableList>
              </div>
            )}
          </div>

          {selectedTickets.size > 0 && (
            <div className="mt-3 mb-[var(--bottom-nav-reserve)] flex shrink-0 flex-col gap-3 border-y border-blue-200 bg-blue-50/40 p-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-2">
                <Icon icon="mdi:check-all" className="shrink-0 text-xl text-blue-600" />
                <div>
                  <div className="font-semibold text-neutral-900">
                    {selectedTickets.size}{" "}
                    {selectedTickets.size === 1
                      ? "ticket seleccionado"
                      : "tickets seleccionados"}
                  </div>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="text-xs text-neutral-500">Subtotal</span>
                    <span className="text-lg font-semibold text-neutral-900">
                      {formatCurrency(selectedTotal)}
                    </span>
                  </div>
                  <div className="mt-0.5 text-xs text-neutral-500">
                    Solo se pueden devolver tickets activos.
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setSelectedTickets(new Set())}
                  className="h-12 flex-1 sm:flex-none"
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={bulkReturn}
                  loading={bulkReturning}
                  className="h-12 flex-1 border-red-200 px-4 text-red-600 hover:bg-red-50 hover:text-red-700 sm:flex-none"
                >
                  {!bulkReturning && (
                    <Icon icon="mdi:undo-variant" className="mr-2 text-xl" />
                  )}
                  Devolver
                </Button>
              </div>
            </div>
          )}
        </section>
      </div>
      </div>

      <RegisterCloseDialog
        open={closeDialogOpen}
        onOpenChange={setCloseDialogOpen}
        onClosed={handleRefetch}
      />
    </PinGuard>
  );
}
